import "i18next";
import React, { useState, useEffect, useRef } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import InfoIcon from "@mui/icons-material/Info";
import PropTypes from "prop-types";
import {
  Typography,
  Box,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Card,
  IconButton,
  Divider,
  CardContent,
  Collapse,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Fade,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowCircleRightOutlinedIcon from "@mui/icons-material/ArrowCircleRightOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SnackBar from "components/SnackBar/SnackBar";
import BackdropComponent from "components/Spinner/Spinner";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import {
  getprojectData,
  getAllSiteCodes,
} from "services/learnPointUserServices";
import {
  searchClassRequestDetailsByParentClient,
  getParentClientWithClassRequestDetails,
  getConfigurationValues,
} from "services/userServices";
import { useTranslation } from "react-i18next";
import { ExpandMoreOutlined } from "@mui/icons-material";
import HelpIcon from "@mui/icons-material/Help";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FileAttachment from "./FileAttachment";
import ClassRequestEdit from "components/ClassRequestEdit/ClassRequestEdit";
import { useDispatch } from "react-redux";
import { generalStoreActions } from "Store/GeneralStore";
import { classRequestActions } from "Store/ClassRequest";
import pillThemeConfig from "styles/pillThemeConfig";
import { parentCLientIdActions } from "../../Store/parentClient";
import { useNavigate, useLocation } from "react-router-dom";
import PrimeReactDataView from "components/PrimeReactDataView/PrimeReactDataView";
import OvernightShippingRequest from "../SearchClassRoster/OvernightShippingRequest";

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      sx: {
        transform: "rotate(0deg)",
      },
    },
    {
      props: ({ expand }) => !!expand,
      sx: {
        transform: "rotate(180deg)",
      },
    },
  ],
}));

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: "0.10em",
  paddingBottom: theme.spacing(1),
  color: theme.palette.primary.main,
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "Urbanist, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  fontWeight: "bold",
  paddingBottom: theme.spacing(0.75),
}));

export default function SearchRequests({ setRequestLength }) {
  const { t } = useTranslation(["SearchRequests"], {
    useSuspense: false,
  });
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // OSR Status Styles
  const osrStatusStyles = {
    Approved: {
      color: "#4CAF50",
      backgroundColor: "#fff",
      icon: (
        <CheckCircleIcon
          sx={{ verticalAlign: "middle", fontSize: 18, mr: 0.5 }}
        />
      ),
    },
    Declined: {
      color: "#F44336",
      backgroundColor: "#fff",
      icon: (
        <CancelIcon sx={{ verticalAlign: "middle", fontSize: 18, mr: 0.5 }} />
      ),
    },
    "Under Approval": {
      color: "#FFA726",
      backgroundColor: "#fff",
      icon: (
        <HourglassEmptyIcon
          sx={{ verticalAlign: "middle", fontSize: 18, mr: 0.5 }}
        />
      ),
    },
    "Pre-approved": {
      color: "#1976D2",
      backgroundColor: "#fff",
      icon: (
        <InfoIcon sx={{ verticalAlign: "middle", fontSize: 18, mr: 0.5 }} />
      ),
    },
  };

  // Constants
  const requestOptions = [
    { key: "edit_request", label: t("menuitem.edit_request") },
    { key: "view_roster", label: t("menuitem.view_roster") },
    { key: "file_attachment", label: t("menuitem.file_attachment") },
    { key: "overnight_shipping", label: t("menuitem.overnight_shipping") },
  ];

  const requestOptionIcons = {
    edit_request: <EditIcon fontSize="small" />,
    view_roster: <VisibilityIcon fontSize="small" />,
    file_attachment: <AttachFileIcon fontSize="small" />,
    overnight_shipping: <LocalShippingIcon fontSize="small" />,
  };

  const requestStatusOptions = [
    { value: "In Progress", label: "In Progress" },
    { value: "On Hold", label: "On Hold" },
    { value: "Closed", label: "Closed" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "Extended Training/Nesting - Full", label: "ETN - Full" },
    { value: "Extended Training/Nesting - Partial", label: "ETN - Partial" },
  ];

  const classTypeOptions = [
    { value: "Attrition", label: "Attrition" },
    { value: "Attrition - Conversion", label: "Attrition - Conversion" },
    { value: "New Launch", label: "New Launch" },
    { value: "New Launch - Conversion", label: "New Launch - Conversion" },
    { value: "Seasonal Growth", label: "Seasonal Growth" },
    {
      value: "Seasonal Growth - Conversion",
      label: "Seasonal Growth - Conversion",
    },
    { value: "Steady State Growth", label: "Steady State Growth" },
    {
      value: "Steady State Growth - Conversion",
      label: "Steady State Growth - Conversion",
    },
  ];

  const sortOptions = [
    { value: "1", label: "Wave: Low to High" },
    { value: "2", label: "Wave: High to Low" },
    { value: "3", label: "RLD: Newest First" },
    { value: "4", label: "RLD: Oldest First" },
    { value: "5", label: "Site: A to Z" },
    { value: "6", label: "Site: Z to A" },
  ];

  const selectOnboardingSupported = [
    { id: 1, name: "OG" },
    { id: 2, name: "FDO" },
    { id: 3, name: "N/A" },
    { id: 4, name: "B&M" },
    { id: 5, name: "AT" },
  ];

  // Loading and UI State
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [createClassRequestNew, setCreateClassRequestNew] = useState(false);

  // Filter State
  const [filters, setFilters] = useState({
    parentClientId: null,
    parentClientCode: "",
    projectCode: "",
    siteCode: "",
    classType: [],
    requestStatus: "In Progress",
    sortOption: "1",
  });

  // Data State
  const [parentClientDetail, setParentClientDetail] = useState([]);
  const [projectDetail, setProjectDetail] = useState([]);
  const [siteDetail, setSiteDetail] = useState([]);
  const [rawClientDetails, setRawClientDetails] = useState([]); // All fetched data
  const [clientDetails, setClientDetails] = useState([]); // Filtered & sorted for display
  const [selectedParentClientDetails, setSelectedParentClientDetails] =
    useState([]);

  // Expansion State
  const [expandedTabIndex, setExpandedTabIndex] = useState(null);
  const [expandedProgramIndex, setExpandedProgramIndex] = useState(null);
  const [expandedProjectIndex, setExpandedProjectIndex] = useState(null);
  const [expandedRosterWaveIndex, setExpandedRosterWaveIndex] = useState(null);

  // Menu State
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAnchorIndex, setMenuAnchorIndex] = useState(null);
  const [menuClicked, setMenuClicked] = useState({});

  // Dialog State
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [selectedAttachmentRequestId, setSelectedAttachmentRequestId] =
    useState(null);
  const [classRequestDialogOpen, setClassRequestDialogOpen] = useState(false);
  const [classRequestDialogType, setClassRequestDialogType] = useState("");
  const [selectedClassRequest, setSelectedClassRequest] = useState([]);

  // Pagination State
  const [totalSearchResultRecords, setTotalSearchResultRecords] = useState(0);
  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 50,
  });

  // Dialog State - Overnight Shipping Request
  const [overnightShippingDialogOpen, setOvernightShippingDialogOpen] =
    useState(false);
  const [overnightShippingClassRequestId, setOvernightShippingClassRequestId] =
    useState(null);
  // Refs
  const isInitialMount = useRef(true);
  const isFirstRenderPagination = useRef(true);

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const snackReset = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  const showError = (message) => {
    setSnack({ open: true, message, severity: "error" });
  };

  const prepareChipStyle = (label) => ({
    fontWeight: "bold",
    backgroundColor: pillThemeConfig[label]?.primaryColor,
    color: pillThemeConfig[label]?.fontColor,
    maxWidth: "100px",
    fontSize: "15px",
    position: "relative",
  });

  // ============================================================================
  // DATA FETCHING FUNCTIONS
  // ============================================================================

  const fetchParentClientDetails = async (requestStatus) => {
    setLoading(true);
    try {
      const response = await getParentClientWithClassRequestDetails();

      if (response?.message === "Success") {
        const filtered = response.data.filter(
          (pc) =>
            (pc.RequestStatus === requestStatus || pc.RequestStatus === null) &&
            pc.Status === true,
        );

        const parentClientMap = new Map();
        filtered.forEach((item) => {
          const key = item.ParentClientCode;
          if (!parentClientMap.has(key)) {
            parentClientMap.set(key, {
              ParentClientId: item.ParentClientId,
              ParentClientCode: item.ParentClientCode,
              ParentClientName: item.ParentClientName,
              Status: item.Status,
              RequestStatus: item.RequestStatus || "NA",
              TotalClassRequest:
                item.TotalClassRequest > 0 ? item.TotalClassRequest : 0,
            });
          } else {
            const existing = parentClientMap.get(key);
            existing.TotalClassRequest +=
              item.TotalClassRequest > 0 ? item.TotalClassRequest : 0;
          }
        });

        const sorted = Array.from(parentClientMap.values()).sort((a, b) => {
          if (b.TotalClassRequest !== a.TotalClassRequest) {
            return b.TotalClassRequest - a.TotalClassRequest;
          }
          return a.ParentClientName.localeCompare(b.ParentClientName);
        });

        setParentClientDetail(sorted);
      }
    } catch (error) {
      showError(t("alert.error_fetching_parent_client_details_"));
    } finally {
      setLoading(false);
    }
  };

  const fetchClassRequestsByParentClient = async (
    parentClientCode,
    requestStatus,
    pageNumber,
    pageSize,
  ) => {
    setLoading(true);
    try {
      const payload = {
        parentClientCode,
        requestStatus,
        pageNumber,
        pageSize,
      };

      const response = await searchClassRequestDetailsByParentClient(payload);
      return response;
    } catch (error) {
      showError(t("alert.error_fetching_parent_client_details_"));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (parentClientId, distinctProjectCodes) => {
    setLoading(true);
    try {
      const response = await getprojectData(parentClientId || 0);

      if (response?.message === "Success") {
        const projects = response.data.map((item) => ({
          ParentClientId: item.ParentClientId,
          ParentClientCode: item.ParentClientCode,
          ProjectId: item.ProjectId,
          ProjectCode: item.ProjectCode,
          ProjectName: item.ProjectName,
          Status: item.Status,
        }));

        const sortedProjects = projects.sort((a, b) =>
          a.ProjectName.localeCompare(b.ProjectName),
        );

        const sortedDistinct = [...distinctProjectCodes].sort(
          (a, b) => b.totalClassRequests - a.totalClassRequests,
        );

        const adjusted = sortedDistinct
          .map((item) => {
            const project = sortedProjects.find(
              (p) => p.ProjectCode === item.projectCode,
            );
            return project
              ? { ...project, totalClassRequests: item.totalClassRequests }
              : null;
          })
          .filter(Boolean)
          .concat(
            sortedProjects.filter(
              (project) =>
                !distinctProjectCodes.some(
                  (item) => item.projectCode === project.ProjectCode,
                ),
            ),
          );

        setProjectDetail(adjusted);
      }
    } catch (error) {
      showError(t("alert.error_fetching_project_details_"));
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteDetails = async (distinctSiteCodes) => {
    setLoading(true);
    try {
      const response = await getAllSiteCodes();

      if (response?.message === "Success") {
        const uniqueSites = new Map();
        response.data.forEach((item) => {
          uniqueSites.set(item.SiteCode, {
            SiteCode: item.SiteCode,
            SiteName: item.SiteName,
          });
        });

        const sites = Array.from(uniqueSites.values());
        const sortedSites = sites.sort((a, b) =>
          a.SiteName.localeCompare(b.SiteName),
        );

        const sortedDistinct = distinctSiteCodes.sort(
          (a, b) => b.totalClassRequests - a.totalClassRequests,
        );

        const adjusted = sortedDistinct
          .map((item) => {
            const site = sites.find((s) => s.SiteCode === item.siteCode);
            return site
              ? { ...site, totalClassRequests: item.totalClassRequests }
              : null;
          })
          .filter(Boolean)
          .concat(
            sortedSites.filter(
              (site) =>
                !distinctSiteCodes.some(
                  (item) => item.siteCode === site.SiteCode,
                ),
            ),
          );

        setSiteDetail(adjusted);
      }
    } catch (error) {
      showError(t("alert.error_fetching_region_details_"));
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // DATA PROCESSING FUNCTIONS
  // ============================================================================

  const extractDistinctCodes = (data) => {
    const projectCodeMap = new Map();
    const siteCodeMap = new Map();

    data.forEach(({ classRequestId, projectCode, siteCode }) => {
      projectCode
        ?.split(",")
        .map((c) => c.trim())
        .forEach((code) => {
          if (!projectCodeMap.has(code)) projectCodeMap.set(code, new Set());
          projectCodeMap.get(code).add(classRequestId);
        });

      siteCode
        ?.split(",")
        .map((s) => s.trim())
        .forEach((code) => {
          if (!siteCodeMap.has(code)) siteCodeMap.set(code, new Set());
          siteCodeMap.get(code).add(classRequestId);
        });
    });

    return {
      distinctProjectCodes: Array.from(projectCodeMap, ([code, ids]) => ({
        projectCode: code,
        totalClassRequests: ids.size,
      })),
      distinctSiteCodes: Array.from(siteCodeMap, ([code, ids]) => ({
        siteCode: code,
        totalClassRequests: ids.size,
      })),
    };
  };

  const applyFilters = (data, filterCriteria) => {
    let filtered = [...data];

    // Filter by project
    if (filterCriteria.projectCode) {
      filtered = filtered.filter((item) => {
        const projectCodes = item.projectCode
          ?.split(",")
          .map((code) => code.trim());
        return projectCodes?.includes(filterCriteria.projectCode);
      });
    }

    // Filter by site
    if (filterCriteria.siteCode) {
      filtered = filtered.filter(
        (item) => item.siteCode === filterCriteria.siteCode,
      );
    }

    // Filter by class type
    if (filterCriteria.classType && filterCriteria.classType.length > 0) {
      filtered = filtered.filter((item) =>
        filterCriteria.classType.includes(item.classType),
      );
    }

    return filtered;
  };

  const applySorting = (data, sortOption) => {
    let sorted = [...data];

    switch (sortOption) {
      case "1":
        sorted.sort((a, b) => a.wave - b.wave);
        break;
      case "2":
        sorted.sort((a, b) => b.wave - a.wave);
        break;
      case "3":
        sorted = sorted
          .map((value) => ({
            ...value,
            rosterDate:
              value.rosterLockDate !== "" ? new Date(value.rosterLockDate) : "",
          }))
          .sort((a, b) => b.rosterDate.valueOf() - a.rosterDate.valueOf());
        break;
      case "4":
        sorted = sorted
          .map((value) => ({
            ...value,
            rosterDate:
              value.rosterLockDate !== "" ? new Date(value.rosterLockDate) : "",
          }))
          .sort((a, b) => a.rosterDate.valueOf() - b.rosterDate.valueOf());
        break;
      case "5":
        sorted.sort((a, b) =>
          a.siteName.toLowerCase().localeCompare(b.siteName.toLowerCase()),
        );
        break;
      case "6":
        sorted.sort((a, b) =>
          b.siteName.toLowerCase().localeCompare(a.siteName.toLowerCase()),
        );
        break;
      default:
        break;
    }

    return sorted;
  };

  const processAndDisplayData = (rawData, filterCriteria) => {
    const filtered = applyFilters(rawData, filterCriteria);
    const sorted = applySorting(filtered, filterCriteria.sortOption);

    setClientDetails(sorted);
    setRequestLength(sorted.length);

    return sorted;
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleParentClientChange = async (newValue) => {
    try {
      if (!newValue) {
        // Reset everything when parent client is cleared
        setFilters({
          parentClientId: null,
          parentClientCode: "",
          projectCode: "",
          siteCode: "",
          classType: [],
          requestStatus: filters.requestStatus,
          sortOption: filters.sortOption,
        });
        setRawClientDetails([]);
        setClientDetails([]);
        setProjectDetail([]);
        setSiteDetail([]);
        setRequestLength(0);
        setTotalSearchResultRecords(0);
        setSelectedParentClientDetails(null);
        localStorage.setItem("parentClientCode", "");
        dispatch(parentCLientIdActions.updateParentClientId(""));
        return;
      }

      // Update filters
      const newFilters = {
        ...filters,
        parentClientId: newValue.ParentClientId,
        parentClientCode: newValue.ParentClientCode,
        projectCode: "",
        siteCode: "",
        classType: [],
      };
      setFilters(newFilters);
      setSelectedParentClientDetails(newValue);
      localStorage.setItem("parentClientCode", newValue.ParentClientCode);
      dispatch(
        parentCLientIdActions.updateParentClientId(newValue.ParentClientCode),
      );

      // Reset pagination
      isFirstRenderPagination.current = true;
      setPaginationParams({ first: 0, rows: 50 });

      // Fetch ALL data at once with a large page size (9999)
      // This allows client-side filtering to work across all records
      const response = await fetchClassRequestsByParentClient(
        newValue.ParentClientCode,
        newFilters.requestStatus,
        1,
        9999,
      );

      if (response?.message === "Success" && response.data) {
        const fetchedData = response.data;
        setRawClientDetails(fetchedData);
        setTotalSearchResultRecords(fetchedData.length);

        // Process and display
        processAndDisplayData(fetchedData, newFilters);

        // Extract and fetch dropdown data
        const { distinctProjectCodes, distinctSiteCodes } =
          extractDistinctCodes(fetchedData);
        await Promise.all([
          fetchProjectDetails(newValue.ParentClientId, distinctProjectCodes),
          fetchSiteDetails(distinctSiteCodes),
        ]);
      }
    } catch (error) {
      showError(t("alert.error_while_performing_cascading_dropdown_"));
    }
  };

  const handleProjectChange = (newValue) => {
    const newFilters = {
      ...filters,
      projectCode: newValue?.ProjectCode || "",
    };
    setFilters(newFilters);

    // Client-side filter only
    processAndDisplayData(rawClientDetails, newFilters);
  };

  const handleSiteChange = (newValue) => {
    const newFilters = {
      ...filters,
      siteCode: newValue?.SiteCode || "",
    };
    setFilters(newFilters);
    dispatch(generalStoreActions.updateSiteLocation(newValue?.SiteCode || ""));

    // Client-side filter only
    processAndDisplayData(rawClientDetails, newFilters);
  };

  const handleRequestStatusChange = async (newValue) => {
    if (!newValue) return;

    try {
      const newFilters = {
        ...filters,
        requestStatus: newValue.value,
        projectCode: "",
        siteCode: "",
        classType: [],
      };
      setFilters(newFilters);

      // Reset data
      setRawClientDetails([]);
      setClientDetails([]);
      setProjectDetail([]);
      setSiteDetail([]);
      setRequestLength(0);

      // Fetch parent clients for new status
      await fetchParentClientDetails(newValue.value);

      // If a parent client is selected, fetch its data
      if (filters.parentClientCode) {
        isFirstRenderPagination.current = true;
        setPaginationParams({ first: 0, rows: 50 });

        // Fetch ALL data at once with a large page size (9999)
        const response = await fetchClassRequestsByParentClient(
          filters.parentClientCode,
          newValue.value,
          1,
          9999,
        );

        if (response?.message === "Success" && response.data) {
          const fetchedData = response.data;
          setRawClientDetails(fetchedData);
          setTotalSearchResultRecords(fetchedData.length);

          processAndDisplayData(fetchedData, newFilters);

          const { distinctProjectCodes, distinctSiteCodes } =
            extractDistinctCodes(fetchedData);
          await Promise.all([
            fetchProjectDetails(filters.parentClientId, distinctProjectCodes),
            fetchSiteDetails(distinctSiteCodes),
          ]);
        }
      }
    } catch (error) {
      showError(t("alert.error_while_changing_request_status_"));
    }
  };

  const handleSortChange = (newValue) => {
    const newFilters = {
      ...filters,
      sortOption: newValue?.value || "1",
    };
    setFilters(newFilters);

    // Client-side sort only
    processAndDisplayData(rawClientDetails, newFilters);
  };

  //class type change
  const handleClassTypeChange = (newValue) => {
    const newFilters = {
      ...filters,
      classType: newValue ? newValue.map((option) => option.value) : [],
    };
    setFilters(newFilters);
    processAndDisplayData(rawClientDetails, newFilters);
  };

  const handlePagination = (event) => {
    // Pagination is now handled client-side by PrimeReact
    // Just update the pagination params for UI state
    setPaginationParams({ first: event.first, rows: event.rows });
  };

  // ============================================================================
  // DIALOG HANDLERS
  // ============================================================================

  const toggleClassRequestDialog = (state, type) => {
    dispatch(generalStoreActions.updateShowProject(false));
    dispatch(classRequestActions.resetClassReq());
    setClassRequestDialogOpen(state);
    setClassRequestDialogType(type);
    if (state === false) {
      dispatch(generalStoreActions.updateClassRequestCreated(true));
    }
  };

  const handleAttachmentDialogOpen = (requestId) => {
    setAttachmentDialogOpen(true);
    setSelectedAttachmentRequestId(requestId);
  };

  const handleAttachmentDialogClose = () => {
    setAttachmentDialogOpen(false);
    setSelectedAttachmentRequestId(null);

    // Refresh data after attachment changes
    if (filters.parentClientCode) {
      handleParentClientChange(selectedParentClientDetails);
    }
  };

  const handleMenuClick = (event, index) => {
    setMenuAnchorIndex(index);
    setMenuAnchorEl(event.currentTarget);
    setMenuClicked((prev) => ({ ...prev, [index]: true }));
  };

  const handleMenuClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      setMenuAnchorIndex(null);
      setMenuAnchorEl(null);
    }, 0);
  };

  // ============================================================================
  // TRANSFORMATION FUNCTIONS
  // ============================================================================

  const transformClassRequestData = (data) => {
    return {
      classRequestId: data.classRequestId ?? null,
      title: data.title ?? null,
      requestStatus: data.requestStatus ?? null,
      parentClient: data.parentClientName ? `${data.parentClientName}` : "",
      client: data.clientName ?? "",
      program: data.programName ?? "",
      project: data.projectName ?? "",
      siteLocation: data.siteName ?? "",
      numberOfClasses: data.numberOfClasses?.toString() ?? "0",
      classRosterCount: data.rosterCount ?? 0,
      wave: data.wave ? data.wave : "",
      taleoRequisitions: data.taleoRequisitions ?? null,
      classType: data.classType ?? "",
      trainingProduction: data.trainingProduction ?? "",
      classFillGoal: data.classFillGoal?.toString() ?? "0",
      productionFillGoalBillable: data.productionFillGoalBillable ?? 0,
      productionFillGoalNonBillable: data.productionFillGoalNonBillable ?? 0,
      trainingClassification: data.trainingClassification ?? "",
      productionClassification: data.productionClassification ?? "",
      hiringBands: data.hiringBands ?? "",
      rosterLockDate: data.rosterLockDate ?? "",
      gpmpoc: data.gpmPoc ?? "",
      createdDate: data.createdDate ?? "",
      createdBy: data.createdBy ?? "",
      siteCode: data.siteCode ?? "",
      projectCode: data.projectCode ?? "",
      modifiedDate: data.modifiedDate ?? "",
      modifiedBy: data.modifiedBy ?? "",
      onboardingSupportedById: data.OnboardingSupportedById,
      classApprovalDate: data.classApprovalDate ?? "",
      classApprovalComments: data.classApprovalComments ?? "",
      opsApprovalDate: data.opsApprovalDate ?? "",
      opsApproverOracleId: data.opsApproverOracleId ?? "",
    };
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Initial load and handle location state
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        // Fetch config value for new UI
        const configResponse = await getConfigurationValues(
          "appsettings:createClassRequestNew",
        );

        if (cancelled) return;

        if (configResponse?.statusCode === 200) {
          if (!cancelled) {
            setCreateClassRequestNew(
              configResponse?.data?.createClassRequestNew.toString() === "1",
            );
          }
        }

        // Check if we have location state with search criteria
        const hasLocationState = location?.state?.searchResultCriteria;
        const requestStatus = hasLocationState
          ? location.state.searchResultCriteria.requestStatus
          : "In Progress";

        // Fetch parent clients
        await fetchParentClientDetails(requestStatus);

        if (cancelled) return;

        // If we have location state, trigger the data load
        if (hasLocationState && isInitialMount.current) {
          const criteria = location.state.searchResultCriteria;

          // Set filters first
          if (!cancelled) {
            setFilters(criteria);
          }

          // Manually fetch the parent client details and trigger data load
          const parentClientResponse =
            await getParentClientWithClassRequestDetails();

          if (cancelled) return;

          if (parentClientResponse?.message === "Success") {
            const filtered = parentClientResponse.data.filter(
              (pc) =>
                (pc.RequestStatus === criteria.requestStatus ||
                  pc.RequestStatus === null) &&
                pc.Status === true,
            );

            const parentClientMap = new Map();
            filtered.forEach((item) => {
              const key = item.ParentClientCode;
              if (!parentClientMap.has(key)) {
                parentClientMap.set(key, {
                  ParentClientId: item.ParentClientId,
                  ParentClientCode: item.ParentClientCode,
                  ParentClientName: item.ParentClientName,
                  Status: item.Status,
                  RequestStatus: item.RequestStatus || "NA",
                  TotalClassRequest:
                    item.TotalClassRequest > 0 ? item.TotalClassRequest : 0,
                });
              } else {
                const existing = parentClientMap.get(key);
                existing.TotalClassRequest +=
                  item.TotalClassRequest > 0 ? item.TotalClassRequest : 0;
              }
            });

            const parentClient = Array.from(parentClientMap.values()).find(
              (pc) => pc.ParentClientCode === criteria.parentClientCode,
            );

            if (parentClient) {
              if (!cancelled) {
                setSelectedParentClientDetails(parentClient);
                localStorage.setItem(
                  "parentClientCode",
                  parentClient.ParentClientCode,
                );
                dispatch(
                  parentCLientIdActions.updateParentClientId(
                    parentClient.ParentClientCode,
                  ),
                );
              }

              // Fetch ALL data
              const response = await fetchClassRequestsByParentClient(
                parentClient.ParentClientCode,
                criteria.requestStatus,
                1,
                9999,
              );

              if (cancelled) return;

              if (response?.message === "Success" && response.data) {
                const fetchedData = response.data;

                if (!cancelled) {
                  setRawClientDetails(fetchedData);
                  setTotalSearchResultRecords(fetchedData.length);

                  // Process and display with the criteria filters
                  processAndDisplayData(fetchedData, criteria);
                }

                // Extract and fetch dropdown data
                const { distinctProjectCodes, distinctSiteCodes } =
                  extractDistinctCodes(fetchedData);

                await Promise.all([
                  fetchProjectDetails(
                    parentClient.ParentClientId,
                    distinctProjectCodes,
                  ),
                  fetchSiteDetails(distinctSiteCodes),
                ]);

                if (cancelled) return;
              }
            }
          }
        }

        if (!cancelled) {
          isInitialMount.current = false;
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to initialize:", error);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  // Skip first pagination render
  useEffect(() => {
    if (isFirstRenderPagination.current) {
      isFirstRenderPagination.current = false;
    }
  }, [paginationParams]);

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const dataViewListItems = (items) => {
    if (!items || items.length === 0) return null;

    const list = items.map((data, index) => {
      return (
        <Card
          variant="outlined"
          key={`${data.classRequestId}-${index}`}
          sx={{
            marginBottom: 2,
            border: "1px solid #e0e0e0",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="top">
              <Grid item xs={12} sm={6} md={1.25}>
                <SubHeading>{t("subheading.class_request_id")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {Number(data.classRequestId)}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {Number(data.classRequestId)}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <SubHeading>{t("subheading.program")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {expandedProgramIndex === index
                        ? data.programName.split(",").map((item, idx) => (
                            <Grid item key={`${idx}-${item}`}>
                              <Typography variant="subtitle1">
                                {item}
                              </Typography>
                            </Grid>
                          ))
                        : data.programName.split(",")[0]}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
                  >
                    {data.programName.split(",").length > 1 ? (
                      <>
                        {expandedProgramIndex === index ? (
                          <>
                            {data.programName.split(",").map((item, idx) => (
                              <Grid item key={`${idx}-${item}`}>
                                <Typography variant="subtitle1">
                                  {item}
                                </Typography>
                              </Grid>
                            ))}
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.click_to_collapse")}
                                </Typography>
                              }
                              placement="right"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  cursor: "pointer",
                                  color: "#3299FF",
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  font: "inherit",
                                }}
                                onClick={(event) => {
                                  setExpandedProgramIndex(null);
                                  event.stopPropagation();
                                }}
                              >
                                {t("iconbutton.all_programs")}
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            {data.programName.split(",")[0]}{" "}
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.click_to_expand")}
                                </Typography>
                              }
                              placement="right"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  cursor: "pointer",
                                  color: "#3299FF",
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  font: "inherit",
                                }}
                                onClick={(event) => {
                                  setExpandedProgramIndex(index);
                                  event.stopPropagation();
                                }}
                              >
                                {t("iconbutton._")}
                                {data.programName.split(",").length - 1}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </>
                    ) : (
                      data.programName
                    )}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={1.75}>
                <SubHeading>{t("subheading.project")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {data.projectName
                        ? expandedProjectIndex === index
                          ? data.projectName.split(",").map((item, idx) => (
                              <Grid item key={`${idx}-${item}`}>
                                <Typography variant="subtitle1">
                                  {item}
                                </Typography>
                              </Grid>
                            ))
                          : data.projectName.split(",")[0]
                        : t("typography.na")}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
                  >
                    {data.projectName &&
                    data.projectName.split(",").length > 1 ? (
                      <>
                        {expandedProjectIndex === index ? (
                          <>
                            {data.projectName.split(",").map((item, idx) => (
                              <Grid item key={`${idx}-${item}`}>
                                <Typography variant="subtitle1">
                                  {item}
                                </Typography>
                              </Grid>
                            ))}
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.click_to_collapse")}
                                </Typography>
                              }
                              placement="right"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  cursor: "pointer",
                                  color: "#3299FF",
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  font: "inherit",
                                }}
                                onClick={(event) => {
                                  setExpandedProjectIndex(null);
                                  event.stopPropagation();
                                }}
                              >
                                {t("iconbutton.all_projects")}
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            {data.projectName.split(",")[0]}{" "}
                            <Tooltip
                              title={
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.click_to_expand")}
                                </Typography>
                              }
                              placement="right"
                              arrow
                            >
                              <IconButton
                                sx={{
                                  cursor: "pointer",
                                  color: "#3299FF",
                                  background: "none",
                                  border: "none",
                                  padding: 0,
                                  font: "inherit",
                                }}
                                onClick={(event) => {
                                  setExpandedProjectIndex(index);
                                  event.stopPropagation();
                                }}
                              >
                                {t("iconbutton._")}
                                {data.projectName.split(",").length - 1}
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </>
                    ) : (
                      data.projectName || t("typography.na")
                    )}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <SubHeading>{t("subheading.site")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {data.siteName}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
                  >
                    {data.siteName}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={0.75}>
                <SubHeading>{t("subheading.wave_")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {data.wave ? data.wave : t("typography.na")}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {data.wave ? data.wave : t("typography.na")}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={6} md={0.75}>
                <SubHeading>{t("subheading.lock_date")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {data.rosterLockDate
                        ? format(new Date(data.rosterLockDate), "dd-MMM-yyyy")
                        : t("typography.na")}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {data.rosterLockDate
                      ? format(new Date(data.rosterLockDate), "dd-MMM-yyyy")
                      : t("typography.na")}
                  </Typography>
                </Tooltip>
              </Grid>
              {data.osrApprovalStatus ? (
                <Grid item xs={12} sm={6} md={1.25}>
                  <SubHeading>{t("subheading.osr_approval_status")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.osrApprovalStatus}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        color:
                          osrStatusStyles[data.osrApprovalStatus]?.color ||
                          "#3F5765",
                        backgroundColor:
                          osrStatusStyles[data.osrApprovalStatus]
                            ?.backgroundColor || "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {osrStatusStyles[data.osrApprovalStatus]?.icon}
                      {data.osrApprovalStatus}
                    </Typography>
                  </Tooltip>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} md={1.25}>
                  <SubHeading>{t("subheading.entered_date_time")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.rosterLockTimestamp
                          ? format(
                              new Date(data.rosterLockTimestamp),
                              "dd-MMM-yyyy hh:mm:ss a",
                            )
                          : t("typography.na")}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.rosterLockTimestamp
                        ? format(
                            new Date(data.rosterLockTimestamp),
                            "dd-MMM-yyyy hh:mm:ss a",
                          )
                        : t("typography.na")}
                    </Typography>
                  </Tooltip>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={2.25}>
                <SubHeading>{t("subheading.modified_by_date_time")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {`${data.modifiedBy ? data.modifiedBy : "NA"} on ${
                        data.modifiedDate
                          ? (() => {
                              try {
                                const date = new Date(data.modifiedDate);
                                return isNaN(date)
                                  ? "NA"
                                  : format(date, "dd-MMM-yyyy hh:mm:ss a");
                              } catch {
                                return "NA";
                              }
                            })()
                          : "NA"
                      }`}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    sx={{ overflowWrap: "break-word", wordBreak: "break-word" }}
                  >
                    {`${data.modifiedBy ? data.modifiedBy : "NA"} on ${
                      data.modifiedDate
                        ? (() => {
                            try {
                              const date = new Date(data.modifiedDate);
                              return isNaN(date)
                                ? "NA"
                                : format(date, "dd-MMM-yyyy hh:mm:ss a");
                            } catch {
                              return "NA";
                            }
                          })()
                        : "NA"
                    }`}
                  </Typography>
                </Tooltip>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                md={0.5}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "top",
                  paddingRight: 0.5,
                }}
              >
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {t("typography.click_for_actions")}
                    </Typography>
                  }
                  placement="bottom"
                  arrow
                >
                  <IconButton
                    sx={{
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                    }}
                    onClick={(event) => handleMenuClick(event, index)}
                  >
                    {!menuClicked[index] ? (
                      <Badge
                        color="primary"
                        variant="dot"
                        sx={{
                          "& .MuiBadge-dot": {
                            backgroundColor: "#3299FF",
                          },
                        }}
                      >
                        <MoreVertIcon />
                      </Badge>
                    ) : (
                      <MoreVertIcon />
                    )}
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={menuAnchorIndex === index}
                  onClose={handleMenuClose}
                  TransitionComponent={Fade}
                  disableScrollLock
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    elevation: 2,
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                      border: "1px solid rgba(0, 0, 0, 0.08)",
                      backgroundColor: "background.paper",
                    },
                  }}
                >
                  {requestOptions
                    .filter(
                      ({ key }) =>
                        key !== "overnight_shipping" || data.isOSRAllowed,
                    )
                    .map(({ key, label }) => {
                      const badgeContent =
                        key === "view_roster"
                          ? data.rosterCount
                          : key === "file_attachment"
                            ? data.fileAttachmentCount
                            : null;
                      const showBadge =
                        badgeContent !== null && badgeContent > 0;
                      const icon = requestOptionIcons[key];
                      return (
                        <MenuItem
                          key={key}
                          onClick={() => {
                            if (key === "edit_request") {
                              dispatch(
                                classRequestActions.addClassRequest(
                                  transformClassRequestData(data),
                                ),
                              );
                              dispatch(
                                classRequestActions.updateParentClientDetails(
                                  selectedParentClientDetails,
                                ),
                              );
                              setSelectedClassRequest(
                                transformClassRequestData(data),
                              );
                              if (createClassRequestNew) {
                                navigate("/home/createclassrequest", {
                                  state: {
                                    mode: "update",
                                    searchResultCriteria: filters,
                                  },
                                });
                              } else {
                                toggleClassRequestDialog(true, "edit");
                              }
                            } else if (key === "view_roster") {
                              const url = `/home/searchrosters?classRequestId=${data.classRequestId}`;
                              window.open(url, "_blank");
                            } else if (key === "file_attachment") {
                              handleAttachmentDialogOpen(data.classRequestId);
                            } else if (key === "overnight_shipping") {
                              setOvernightShippingClassRequestId(
                                data.classRequestId,
                              );
                              setOvernightShippingDialogOpen(true);
                            }
                            handleMenuClose();
                          }}
                          sx={{
                            py: 1,
                            px: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                mr: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {icon}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "1rem",
                                fontWeight: 500,
                              }}
                            >
                              {label}
                            </Typography>
                            {showBadge && (
                              <Chip
                                label={badgeContent}
                                size="small"
                                sx={{
                                  height: 21,
                                  minWidth: 21,
                                  backgroundColor: "#3299FF",
                                  color: "white",
                                }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                </Menu>
              </Grid>
              <Grid item xs={1.25}>
                <SubHeading>{t("subheading.class_type")}</SubHeading>
                <Tooltip
                  title={
                    <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                      {data.classType || "NA"}
                    </Typography>
                  }
                  placement="bottom-start"
                  arrow
                >
                  <Typography variant="subtitle1" gutterBottom>
                    {data.classType || "NA"}
                  </Typography>
                </Tooltip>
              </Grid>
              {data.waveRosterJSON.length === 0 && (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    marginTop: 1,
                  }}
                >
                  <ExpandMore
                    expand={expandedTabIndex === index}
                    onClick={() =>
                      setExpandedTabIndex(
                        expandedTabIndex === index ? null : index,
                      )
                    }
                    aria-expanded={expandedTabIndex === index}
                    aria-label={t("aria-label.show_more")}
                  >
                    <Tooltip
                      title={
                        <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                          {expandedTabIndex === index
                            ? t("typography.click_to_collapse")
                            : t("typography.click_to_expand")}
                        </Typography>
                      }
                      placement="bottom"
                      arrow
                    >
                      <ExpandMoreOutlined />
                    </Tooltip>
                  </ExpandMore>
                </Grid>
              )}
              {data.waveRosterJSON.length > 0 && (
                <>
                  <Grid
                    item
                    xs={1.75}
                    sx={{
                      display: "flex",
                      alignItems: "top",
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <SubHeading>{t("subheading.roster_id_wave")}</SubHeading>
                      <SubHeading>{t("subheading.active_ftrr_e2p")}</SubHeading>
                    </Box>
                    <Tooltip
                      title={
                        <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                          {t(
                            "typography.this_section_displays_detailed_information_about_the_class_roster_including_the_roster_id_wave_number_active_trainee_count_ftrr_count_and_e2p_count_",
                          )}
                        </Typography>
                      }
                      placement="bottom"
                      arrow
                    >
                      <HelpIcon sx={{ color: "#7D71E2", marginLeft: 1 }} />
                    </Tooltip>
                  </Grid>
                  <Grid
                    item
                    xs={8}
                    container
                    spacing={1}
                    direction="row"
                    wrap="wrap"
                  >
                    {data.waveRosterJSON
                      .slice(
                        0,
                        expandedRosterWaveIndex === index
                          ? data.waveRosterJSON.length
                          : 10,
                      )
                      .map((waveRosterData, idx) => (
                        <Grid
                          item
                          xs={1}
                          key={`${waveRosterData.classRosterId}-${index}`}
                        >
                          <Tooltip
                            title={
                              <React.Fragment>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.roster_id_")}{" "}
                                  {waveRosterData.classRosterId}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.wave_number_")}{" "}
                                  {waveRosterData.waveNumber}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.active_trainee_")}{" "}
                                  {waveRosterData.activeCount}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.ftrr_count_")}
                                  {waveRosterData.ftrrCount}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "1rem" }}
                                >
                                  {t("typography.e2p_count_")}
                                  {waveRosterData.etpCount}
                                </Typography>
                              </React.Fragment>
                            }
                            placement="bottom"
                            arrow
                          >
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{ textAlign: "center" }}
                              >
                                {waveRosterData.classRosterId}
                                {t("typography._")} {waveRosterData.waveNumber}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                sx={{ textAlign: "center" }}
                              >
                                <Box component="span" sx={{ color: "#3299FF" }}>
                                  {waveRosterData.activeCount}
                                </Box>{" "}
                                {t("typography._")}{" "}
                                <Box
                                  component="span"
                                  sx={{
                                    color:
                                      waveRosterData.ftrrCount ===
                                      waveRosterData.activeCount
                                        ? "#3299FF"
                                        : "#ba000d",
                                  }}
                                >
                                  {waveRosterData.ftrrCount}
                                </Box>{" "}
                                {t("typography._")}{" "}
                                <Box
                                  component="span"
                                  sx={{
                                    color:
                                      waveRosterData.etpCount ===
                                      waveRosterData.activeCount
                                        ? "#3299FF"
                                        : "#ba000d",
                                  }}
                                >
                                  {waveRosterData.etpCount}
                                </Box>
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Grid>
                      ))}
                    {data.waveRosterJSON.length > 10 && (
                      <Grid
                        item
                        xs={0.85}
                        sx={{
                          alignItems: "top",
                          textAlign: "center",
                        }}
                      >
                        {expandedRosterWaveIndex === index ? (
                          <Tooltip
                            title={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "1rem" }}
                              >
                                {t("typography.click_to_collapse")}
                              </Typography>
                            }
                            placement="bottom"
                            arrow
                          >
                            <IconButton
                              sx={{
                                cursor: "pointer",
                                color: "#3299FF",
                                background: "none",
                                border: "none",
                                padding: 0,
                                font: "inherit",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRosterWaveIndex(null);
                              }}
                            >
                              {t("iconbutton.all_rosters")}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "1rem" }}
                              >
                                {t("typography.click_to_expand")}
                              </Typography>
                            }
                            placement="bottom"
                            arrow
                          >
                            <IconButton
                              sx={{
                                cursor: "pointer",
                                color: "#3299FF",
                                background: "none",
                                border: "none",
                                padding: 0,
                                font: "inherit",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRosterWaveIndex(index);
                              }}
                            >
                              {t("iconbutton._")}
                              {data.waveRosterJSON.length - 10}{" "}
                              {t("iconbutton.more")}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={0.75}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-end",
                      marginTop: 1,
                    }}
                  >
                    <ExpandMore
                      expand={expandedTabIndex === index}
                      onClick={() =>
                        setExpandedTabIndex(
                          expandedTabIndex === index ? null : index,
                        )
                      }
                      aria-expanded={expandedTabIndex === index}
                      aria-label={t("aria-label.show_more")}
                    >
                      <Tooltip
                        title={
                          <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                            {expandedTabIndex === index
                              ? t("typography.click_to_collapse")
                              : t("typography.click_to_expand")}
                          </Typography>
                        }
                        placement="bottom"
                        arrow
                      >
                        <ExpandMoreOutlined />
                      </Tooltip>
                    </ExpandMore>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
          <Collapse
            in={expandedTabIndex === index}
            timeout="auto"
            unmountOnExit
          >
            <CardContent sx={{ paddingTop: 0 }}>
              <Grid container spacing={2} alignItems="top">
                <Grid item xs={1.25}>
                  <SubHeading>
                    {t("subheading.training_classification")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.trainingClassification || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.trainingClassification || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>
                    {t("subheading.production_classification")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.productionClassification || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.productionClassification || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={0.75}>
                  <SubHeading>{t("subheading.no_of_classes")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {Number(data.numberOfClasses) || 0}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {Number(data.numberOfClasses) || 0}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <SubHeading>{t("subheading.class_fill_goal")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {Number(data.classFillGoal) || 0}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {Number(data.classFillGoal) || 0}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>{t("subheading.Nesting_fill_goal")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {Number(data.nestingFillGoal) || 0}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {Number(data.nestingFillGoal) || 0}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <SubHeading>{t("subheading.request_status")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.requestStatus || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.requestStatus || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>{t("subheading.gpm_poc")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.gpmpoc || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.gpmpoc || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={0.75}>
                  <SubHeading>{t("subheading.hiring_bands")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.hiringBands || "No"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.hiringBands || "No"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={2.25}>
                  <SubHeading>
                    {t("subheading.training_produciton_solutions")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.trainingProduction || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.trainingProduction || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>
                    {t("subheading.production_fill_goal")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {Number(data.productionFillGoalBillable) +
                          Number(data.productionFillGoalNonBillable) || 0}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {Number(data.productionFillGoalBillable) +
                        Number(data.productionFillGoalNonBillable) || 0}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>
                    {t("subheading.onboarding_supported")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {selectOnboardingSupported
                          .find(
                            (item) =>
                              item?.id?.toString() ===
                              data?.OnboardingSupportedById?.toString(),
                          )
                          ?.name.toString() ?? "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {selectOnboardingSupported
                        .find(
                          (item) =>
                            item?.id?.toString() ===
                            data?.OnboardingSupportedById?.toString(),
                        )
                        ?.name.toString() ?? "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>{t("subheading.class_approval_date")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.classApprovalDate
                          ? format(
                              new Date(data.classApprovalDate),
                              "dd-MMM-yyyy",
                            )
                          : t("typography.na")}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.classApprovalDate
                        ? format(
                            new Date(data.classApprovalDate),
                            "dd-MMM-yyyy",
                          )
                        : t("typography.na")}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.7}>
                  <SubHeading>
                    {t("subheading.class_approval_comments")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.classApprovalComments || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.classApprovalComments || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>{t("subheading.taleo_requisitions")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.taleoRequisitions || "NA"}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.taleoRequisitions || "NA"}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1.25}>
                  <SubHeading>{t("subheading.ops_approval_date")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.opsApprovalDate
                          ? format(
                              new Date(data.opsApprovalDate),
                              "dd-MMM-yyyy",
                            )
                          : t("typography.na")}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.opsApprovalDate
                        ? format(new Date(data.opsApprovalDate), "dd-MMM-yyyy")
                        : t("typography.na")}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={1}>
                  <SubHeading>{t("subheading.ops_approver")}</SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {data.opsApproverOracleId
                          ? `${data.opsApproverName} (${data.opsApproverOracleId})`
                          : t("typography.na")}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {data.opsApproverOracleId
                        ? `${data.opsApproverName} (${data.opsApproverOracleId})`
                        : t("typography.na")}
                    </Typography>
                  </Tooltip>
                </Grid>
                <Grid item xs={2.75}>
                  <SubHeading>
                    {t("subheading.created_by_date_time")}
                  </SubHeading>
                  <Tooltip
                    title={
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {`${data.createdBy ? data.createdBy : "NA"} on ${
                          data.createdDate
                            ? (() => {
                                try {
                                  const date = new Date(data.createdDate);
                                  return isNaN(date)
                                    ? "NA"
                                    : format(date, "dd-MMM-yyyy hh:mm:ss a");
                                } catch {
                                  return "NA";
                                }
                              })()
                            : "NA"
                        }`}
                      </Typography>
                    }
                    placement="bottom-start"
                    arrow
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      {`${data.createdBy ? data.createdBy : "NA"} on ${
                        data.createdDate
                          ? (() => {
                              try {
                                const date = new Date(data.createdDate);
                                return isNaN(date)
                                  ? "NA"
                                  : format(date, "dd-MMM-yyyy hh:mm:ss a");
                              } catch {
                                return "NA";
                              }
                            })()
                          : "NA"
                      }`}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      );
    });

    return <div className="grid grid-nogutter">{list}</div>;
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      {loading && <BackdropComponent />}

      <FileAttachment
        attachmentMode="ClassRequest"
        setLoading={setLoading}
        setSnack={setSnack}
        classRequestId={selectedAttachmentRequestId}
        classRosterId={null}
        attachmentDialogOpen={attachmentDialogOpen}
        handleAttachmentDialogClose={handleAttachmentDialogClose}
      />

      <Dialog
        open={classRequestDialogOpen}
        onClose={() => toggleClassRequestDialog(false, classRequestDialogType)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <MainHeading>
                {classRequestDialogType === "add"
                  ? t("mainheading.add_class_request")
                  : t("mainheading.edit_class_request")}
              </MainHeading>
              <IconButton
                aria-label={t("aria-label.close")}
                onClick={() =>
                  toggleClassRequestDialog(false, classRequestDialogType)
                }
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
            >
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                    t("ogTitle")
                  </Typography>
                }
                arrow
              >
                <Chip label={t("label.og")} sx={prepareChipStyle("OG")}></Chip>
              </Tooltip>
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                    t("ldTitle")
                  </Typography>
                }
                arrow
              >
                <Chip label={t("label.l_d")} sx={prepareChipStyle("LD")}></Chip>
              </Tooltip>
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                    t("wfmTitle")
                  </Typography>
                }
                arrow
              >
                <Chip
                  label={t("label.wfm")}
                  sx={prepareChipStyle("WFM")}
                ></Chip>
              </Tooltip>
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                    t("taTitle")
                  </Typography>
                }
                arrow
              >
                <Chip label={t("label.ta")} sx={prepareChipStyle("TA")}></Chip>
              </Tooltip>
              <Tooltip
                title={
                  <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                    t("opsTitle")
                  </Typography>
                }
                arrow
              >
                <Chip
                  label={t("label.ops")}
                  sx={prepareChipStyle("OPS")}
                ></Chip>
              </Tooltip>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <ClassRequestEdit
            type={classRequestDialogType}
            classRequestDetails={
              classRequestDialogType === "edit" ? selectedClassRequest : null
            }
            onClose={() =>
              toggleClassRequestDialog(false, classRequestDialogType)
            }
            onUpdate={() =>
              handleParentClientChange(selectedParentClientDetails)
            }
          />
        </DialogContent>
      </Dialog>

      <Grid container spacing={1}>
        <Grid item xs={2.05}>
          <SubHeading>{t("subheading.parent_client")}</SubHeading>
          <Autocomplete
            disablePortal
            blurOnSelect
            options={parentClientDetail}
            getOptionLabel={(option) => option.ParentClientName}
            value={
              parentClientDetail.find(
                (option) =>
                  option.ParentClientCode === filters.parentClientCode,
              ) || null
            }
            onChange={(event, newValue) => handleParentClientChange(newValue)}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.ParentClientCode}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <ListItemText primary={option.ParentClientName} />
                {option.TotalClassRequest !== 0 && (
                  <Chip
                    label={option.TotalClassRequest}
                    size="small"
                    sx={{
                      backgroundColor: "#3299FF",
                      color: "white",
                      letterSpacing: "0.10em",
                    }}
                  />
                )}
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.parent_client")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={0.25}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            paddingBottom: 0.25,
          }}
        >
          <ArrowCircleRightOutlinedIcon
            sx={{ color: "#3299FF", height: 32, width: 32 }}
          />
        </Grid>
        <Grid item xs={2.05}>
          <SubHeading>{t("subheading.project")}</SubHeading>
          <Autocomplete
            disablePortal
            blurOnSelect
            options={projectDetail}
            getOptionLabel={(option) => option.ProjectName}
            value={
              projectDetail.find(
                (option) => option.ProjectCode === filters.projectCode,
              ) || null
            }
            onChange={(event, newValue) => handleProjectChange(newValue)}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.ProjectCode}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <ListItemText primary={option.ProjectName} />
                {option.totalClassRequests && (
                  <Chip
                    label={option.totalClassRequests}
                    size="small"
                    sx={{
                      backgroundColor: "#3299FF",
                      color: "white",
                      letterSpacing: "0.10em",
                    }}
                  />
                )}
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.project")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={2.05}>
          <SubHeading>{t("subheading.site")}</SubHeading>
          <Autocomplete
            disablePortal
            blurOnSelect
            options={siteDetail}
            getOptionLabel={(option) => option.SiteName}
            value={
              siteDetail.find(
                (option) => option.SiteCode === filters.siteCode,
              ) || null
            }
            onChange={(event, newValue) => handleSiteChange(newValue)}
            renderOption={(props, option) => (
              <ListItem
                {...props}
                key={option.SiteCode}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <ListItemText primary={option.SiteName} />
                {option.totalClassRequests && (
                  <Chip
                    label={option.totalClassRequests}
                    size="small"
                    sx={{
                      backgroundColor: "#3299FF",
                      color: "white",
                      letterSpacing: "0.10em",
                    }}
                  />
                )}
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.site")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={1.35}>
          <SubHeading>{t("subheading.class_type")}</SubHeading>
          <Autocomplete
            multiple
            disablePortal
            blurOnSelect
            disableCloseOnSelect
            options={classTypeOptions}
            getOptionLabel={(option) => option.label}
            value={classTypeOptions.filter((option) =>
              filters.classType.includes(option.value),
            )}
            onChange={(event, newValue) => handleClassTypeChange(newValue)}
            renderTags={() => null}
            renderInput={(params) => {
              return (
                <Tooltip
                  title={
                    filters.classType.length > 0 ? (
                      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
                        {classTypeOptions
                          .filter((option) =>
                            filters.classType.includes(option.value),
                          )
                          .map((option) => option.label)
                          .join(", ")}
                      </Typography>
                    ) : (
                      ""
                    )
                  }
                  placement="bottom"
                  arrow
                >
                  <TextField
                    {...params}
                    placeholder={
                      filters.classType.length === 0
                        ? t("subheading.class_type")
                        : classTypeOptions
                            .filter((option) =>
                              filters.classType.includes(option.value),
                            )
                            .map((option) => option.label)
                            .join(", ")
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      "& input::placeholder": {
                        fontWeight: filters.classType.length > 0 ? 400 : 100,
                        opacity: 1,
                      },
                    }}
                  />
                </Tooltip>
              );
            }}
          />
        </Grid>
        <Grid item xs={1.2}>
          <SubHeading>{t("subheading.request_status")}</SubHeading>
          <Autocomplete
            freeSolo
            disablePortal
            blurOnSelect
            options={requestStatusOptions}
            getOptionLabel={(option) => option.label}
            value={
              requestStatusOptions.find(
                (option) => option.value === filters.requestStatus,
              ) || null
            }
            onChange={(event, newValue) => handleRequestStatusChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.request_status")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={1.55}>
          <SubHeading>{t("subheading.sort_by")}</SubHeading>
          <Autocomplete
            freeSolo
            disablePortal
            blurOnSelect
            options={sortOptions}
            getOptionLabel={(option) => option.label}
            value={
              sortOptions.find(
                (option) => option.value === filters.sortOption,
              ) || null
            }
            onChange={(event, newValue) => handleSortChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("placeholder.sort_by")}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Grid>
        <Grid
          item
          xs={1.5}
          sx={{
            display: "flex",
            alignItems: "flex-end",
            paddingBottom: 0.25,
          }}
        >
          {filters.parentClientCode && (
            <Tooltip title={t("custombutton.add_class_request")}>
              <CustomButton
                variant="contained"
                startIcon={<AddCircleOutlineOutlinedIcon />}
                onClick={() => {
                  if (createClassRequestNew === true) {
                    dispatch(
                      classRequestActions.updateParentClientDetails(
                        selectedParentClientDetails,
                      ),
                    );
                    dispatch(classRequestActions.resetClassReq());
                    navigate("/home/createclassrequest", {
                      state: {
                        mode: "create",
                        searchResultCriteria: filters,
                      },
                    });
                  } else {
                    toggleClassRequestDialog(true, "add");
                  }
                }}
              >
                {t("custombutton.add_class_request")}
              </CustomButton>
            </Tooltip>
          )}
        </Grid>
        {clientDetails.length > 0 ? (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
                maxHeight: "calc(100vh - 300px)",
                position: "relative",
                zIndex: 0,
                transform: "translateZ(0)",
              }}
            >
              <PrimeReactDataView
                value={clientDetails}
                listTemplate={dataViewListItems}
                paginator
                rows={paginationParams?.rows || 50}
                totalRecords={clientDetails.length}
                lazy={false}
                first={paginationParams.first}
                onPage={handlePagination}
                rowsPerPageOptions={[50, 100, 200]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "calc(100vh - 425px)",
                width: "100%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#3F5765",
                  fontWeight: "bold",
                  letterSpacing: "0.10em",
                  padding: 2,
                }}
              >
                {t("typography.no_class_requests_found_")}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#3F5765",
                  letterSpacing: "0.05em",
                  padding: 1,
                }}
              >
                {t(
                  "typography.please_select_a_parent_client_and_other_parameters_to_fetch_class_requests_",
                )}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      <SnackBar snack={snack} snackReset={snackReset} />
      <OvernightShippingRequest
        open={overnightShippingDialogOpen}
        classRequestId={overnightShippingClassRequestId}
        parentClientId={filters?.parentClientCode} //filters.parentClientCode
        parentClientName={selectedParentClientDetails?.ParentClientName}
        onClose={() => setOvernightShippingDialogOpen(false)}
        onSubmit={() => {          
          if (filters.parentClientCode) {
            handleParentClientChange(selectedParentClientDetails);
          }
        }}
      />
    </>
  );
}

SearchRequests.propTypes = {
  setRequestLength: PropTypes.func,
};
